import { useEffect, useState } from "react";
import { Card, Box } from "@mui/material";
import { useAppSelector } from "../../../../redux/hooks";
import { getMany } from "../../../../redux/features/dynamic/actions";
import { In, IQueryCommandOptions } from "@llws/dynamic-router-utils";
import { makeYmd, getMonthStart, getMonthEnd, dateFromYmd } from "src/app-utils";
import { intVal } from "@jamesgmarks/utilities";
import TimelineFilterControls from "./components/TimelineFilterControls";
import APTUTimeline from "./components/APTUTimeline";
import APTUTable from "./components/APTUTable";
import SingleUnitInitialPeriodsTable from "./components/SingleUnitInitialPeriodsTable";
import { IActivePropertyTimesUnits } from "@llws/lift-entity-interfaces";

// TODO: I am deeply ashamed of this code, but it works and I don't have time to refactor it right now.
// Please forgive me, future developer.

export type TimelineDataType = Array<Array<string | Date | { type: string; id: string; role?: string }>>;

// Add sort types
export type SortDirection = 'asc' | 'desc';
export type SortField = 'id' | 'buildingId' | 'building.buildingName' | 'suiteTypeId' |
'buildingBillingTypeId' | 'startTime' | 'stopTime' | 'activeUnits';

// Define color constants for better consistency
const COLORS = {
  ACTIVE_APTU: '#34a853',  // Green for active APTUs
  INACTIVE_APTU: '#4285f4', // Blue for inactive APTUs
  EXTENDED_APTU: '#d32f2f', // Red for extended periods
  INITIAL_PERIOD: '#9C27B0', // Purple for initial periods
  WARNING: '#FFA500',      // Orange for warnings
};

const TieredUnitCountTimelineContainer = () => {
  const [buildingId, setBuildingId] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(
    getMonthStart(new Date().getFullYear(), new Date().getMonth() + 1),
  );
  const [endDate, setEndDate] = useState<Date | null>(getMonthEnd(new Date().getFullYear(), new Date().getMonth() + 1));
  const [clampDates, setClampDates] = useState<boolean>(false);
  const aptuData = useAppSelector((store) => store.dynamic.data.active_property_times_units?.list || []);
  const singleUnitInitialPeriodsData = useAppSelector(
    (store) => store.dynamic.data.single_unit_initial_periods?.list || [],
  );

  const [sortField, setSortField] = useState<SortField>('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const [timelineData, setTimelineData] = useState<TimelineDataType>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Constants
  const PARTNER_ID = 66;

  const fetchSingleUnitInitialPeriods = (suiteTypeIds: (number | null | undefined)[], buildingId: string) => {
    const validSuiteTypeIds = suiteTypeIds.map(intVal).filter((st)=>!Number.isNaN(st));
    if (validSuiteTypeIds.length === 0 || !buildingId || !startDate || !endDate) {
      return;
    }

    const formattedStartDate = makeYmd(startDate);
    const formattedEndDate = makeYmd(endDate);

    const suipQueryOptions: IQueryCommandOptions = {
      where: [
        { field: 'buildingId', value: buildingId, condition: 'and' },
        In('suiteTypeId', validSuiteTypeIds ),
        // Filter initial periods that overlap with the selected date range
        { field: 'endDate', value: formattedStartDate, operator: 'gte', condition: 'and' },
        { field: 'startDate', value: formattedEndDate, operator: 'lte', condition: 'and' },
      ],
      limit: 1000,
    };

    return getMany('single_unit_initial_periods', suipQueryOptions);
  };

  const fetchAPTUData = () => {
    if (!startDate || !endDate) {
      console.error("Start date and end date must be selected.");
      return;
    }

    setIsLoading(true);
    const partnerId = `${PARTNER_ID}`;
    const _buildingId = `${buildingId}`;
    const formattedStartDate = makeYmd(startDate);
    const formattedEndDate = makeYmd(endDate);

    const queryCommandOptions: IQueryCommandOptions = {
      where: [
        { field: 'partnerId', value: partnerId, condition: 'and' },
        { field: 'buildingId', value: _buildingId, condition: 'and' },
        { field: 'startTime', value: formattedEndDate, operator: 'lte', condition: 'and' },
        {
          whereCommands: [
            { field: 'stopTime', value: formattedStartDate, operator: 'gte', condition: 'and' },
            { field: 'stopTime', value: '', operator: 'null', condition: 'or' },
          ],
          condition: 'and',
        },
      ],
      limit: 1000,
    };

    getMany('active_property_times_units', queryCommandOptions)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (aptuData.length > 0 && buildingId) {
      const suiteTypeIds = aptuData.map(aptu => aptu.suiteTypeId);
      fetchSingleUnitInitialPeriods(suiteTypeIds, buildingId);
    }
  }, [aptuData, buildingId]);

  useEffect(() => {
    if (aptuData.length <= 0) {
      return;
    }

    const columns = [
      { type: "string", id: "Suite Type" },
      { type: "string", id: "Building" },
      { type: "string", id: "style", role: "style" },
      { type: "date", id: "Start" },
      { type: "date", id: "End" },
      { type: "string", id: "tooltip", role: "tooltip", p: { html: true } },
    ];

    // Create a map of suite type IDs to their initial periods for quick lookups
    const suiteTypeToInitialPeriods = new Map();
    singleUnitInitialPeriodsData.forEach(period => {
      if (!period.startDate || !period.endDate || !period.suiteTypeId) return;

      if (!suiteTypeToInitialPeriods.has(period.suiteTypeId)) {
        suiteTypeToInitialPeriods.set(period.suiteTypeId, []);
      }
      suiteTypeToInitialPeriods.get(period.suiteTypeId).push({
        start: dateFromYmd(period.startDate),
        end: dateFromYmd(period.endDate),
      });
    });

    const createSegment = (
      suiteTypeLabel: string,
      buildingLabel: string,
      style: string,
      start: Date,
      end: Date,
      tooltip: string,
    ) => ({
      suiteType: suiteTypeLabel,
      building: buildingLabel,
      style,
      start,
      end,
      tooltip,
    });

    const createTooltip = (title: string, details: Record<string, string | number | undefined>) => {
      const detailRows = Object.entries(details)
        .map(([key, value]) => `${key}: ${value || 'N/A'}`)
        .join('<br>');
      return `<div style="padding: 10px;">
        <strong>${title}</strong><br>
        ${detailRows}
      </div>`;
    };

    // Function to create APTU timeline segments
    const createAPTUTimelineRows = (aptu:IActivePropertyTimesUnits) => {
      const aptuStartDate = aptu.startTime ? dateFromYmd(aptu.startTime) : new Date();
      const aptuEndDate = aptu.stopTime ? dateFromYmd(aptu.stopTime) : new Date();

      const clampedStartDate = (clampDates && startDate && aptuStartDate < startDate) ? startDate : aptuStartDate;
      const clampedEndDate = (clampDates && endDate && aptuEndDate > endDate) ? endDate : aptuEndDate;

      const suiteTypeId = aptu.suiteTypeId;
      const buildingLabel = (
        `Building ${aptu.buildingId} ${aptu.building?.buildingName || ""} - ${aptu.activeUnits} active units`
      );
      const suiteTypeLabel = `Suite Type ${suiteTypeId || 'Unknown'}`;

      // If no initial periods for this suite type, show as warning
      if (!suiteTypeId || !suiteTypeToInitialPeriods.has(suiteTypeId)) {
        // Warning message specifying no period found in range
        const warningMessage = (
          `WARNING: No Initial Period Found in Selected Date Range for Suite Type ${suiteTypeId || 'Unknown'}`
        );

        return [
          createSegment(
            suiteTypeLabel,
            warningMessage,
            `color: ${COLORS.WARNING}; opacity: 1; font-weight: bold`,
            clampedStartDate,
            clampedEndDate,
            '',
          ),
        ];
      }

      const initialPeriods = suiteTypeToInitialPeriods.get(suiteTypeId);
      const segments = [];
      let hasOverlap = false;

      // Check for overlaps with initial periods
      for (const period of initialPeriods) {
        // If APTU is completely outside this initial period, skip
        if (clampedEndDate <= period.start || clampedStartDate >= period.end) {
          continue;
        }

        hasOverlap = true;

        // If APTU starts before initial period
        if (clampedStartDate < period.start) {
          const daysBefore = Math.floor((period.start.getTime() - clampedStartDate.getTime()) / (1000 * 60 * 60 * 24));

          segments.push(
            createSegment(
              suiteTypeLabel,
              `${buildingLabel} (${daysBefore} days before initial period)`,
              `color: ${COLORS.EXTENDED_APTU}; opacity: 0.7`,
              clampedStartDate,
              period.start,
              createTooltip('Extended Period (Before Initial)', {
                Building: `${aptu.buildingId} ${aptu.building?.buildingName || ""}`,
                'Suite Type': suiteTypeId,
                Units: aptu.activeUnits,
                Extended: `${daysBefore} days before initial period`,
              }),
            ),
          );
        }

        // The part of APTU inside the initial period
        const overlapStart = clampedStartDate < period.start ? period.start : clampedStartDate;
        const overlapEnd = clampedEndDate > period.end ? period.end : clampedEndDate;

        segments.push(
          createSegment(
            suiteTypeLabel,
            buildingLabel,
            `color: ${aptu.stopTime ? COLORS.INACTIVE_APTU : COLORS.ACTIVE_APTU}`,
            overlapStart,
            overlapEnd,
            createTooltip('Within Initial Period', {
              Building: `${aptu.buildingId} ${aptu.building?.buildingName || ""}`,
              'Suite Type': suiteTypeId,
              Units: aptu.activeUnits,
              Period: 'Within initial period boundary',
            }),
          ),
        );

        // If APTU extends past the initial period
        if (clampedEndDate > period.end) {
          const daysAfter = Math.floor((clampedEndDate.getTime() - period.end.getTime()) / (1000 * 60 * 60 * 24));

          segments.push(
            createSegment(
              suiteTypeLabel,
              `${buildingLabel} (${daysAfter} days after initial period)`,
              `color: ${COLORS.EXTENDED_APTU}; opacity: 0.7`,
              period.end,
              clampedEndDate,
              createTooltip('Extended Period (After Initial)', {
                Building: `${aptu.buildingId} ${aptu.building?.buildingName || ""}`,
                'Suite Type': suiteTypeId,
                Units: aptu.activeUnits,
                Extended: `${daysAfter} days after initial period`,
              }),
            ),
          );
        }
      }

      // If no overlap with any initial period, show the whole bar as extended
      if (!hasOverlap) {
        const tooltip = createTooltip('No Overlap With Initial Period', {
          Building: `${aptu.buildingId} ${aptu.building?.buildingName || ""}`,
          'Suite Type': suiteTypeId,
          Units: aptu.activeUnits,
          Period: (
            `${aptuStartDate.toLocaleDateString()} to ${aptu.stopTime ? aptuEndDate.toLocaleDateString() : 'Ongoing'}`
          ),
        });

        segments.push(
          createSegment(
            suiteTypeLabel,
            buildingLabel,
            `color: ${COLORS.EXTENDED_APTU}; opacity: 0.7`,
            clampedStartDate,
            clampedEndDate,
            tooltip,
          ),
        );
      }

      return segments;
    };

    // Process all APTUs and create segmented timeline data
    const allAptuSegments = aptuData.flatMap(createAPTUTimelineRows);

    // Convert segments to timeline rows format
    const aptuRows = allAptuSegments.map(segment => [
      segment.suiteType,
      segment.building,
      segment.style,
      segment.start,
      segment.end,
      segment.tooltip,
    ]);

    // Create rows for SingleUnitInitialPeriod data
    const suipRows = singleUnitInitialPeriodsData
      .filter(period => aptuData.some(aptu => aptu.suiteTypeId === period.suiteTypeId))
      .map(period => {
        if (!period.startDate || !period.endDate) {
          return null;
        }

        const periodStartDate = dateFromYmd(period.startDate);
        const periodEndDate = dateFromYmd(period.endDate);

        const clampedStartDate = (clampDates && startDate && periodStartDate < startDate) ? startDate : periodStartDate;
        const clampedEndDate = (clampDates && endDate && periodEndDate > endDate) ? endDate : periodEndDate;

        const daysCount = Math.floor((periodEndDate.getTime() - periodStartDate.getTime()) / (1000 * 60 * 60 * 24));

        const tooltip = `<div style="padding: 10px;">
          <strong>Initial Period</strong><br>
          Suite Type: ${period.suiteTypeId}<br>
          Start: ${periodStartDate.toLocaleDateString()}<br>
          End: ${periodEndDate.toLocaleDateString()}<br>
          Duration: ${daysCount} days
        </div>`;

        return [
          `Suite Type ${period.suiteTypeId || 'Unknown'}`,
          `Initial Period: ${period.startDate.substring(0, 10)} to ${period.endDate.substring(0, 10)}`,
          `color: ${COLORS.INITIAL_PERIOD}; opacity: 0.7`,
          clampedStartDate,
          clampedEndDate,
          tooltip,
        ];
      })
      .filter((row): row is (string | Date)[] => row !== null);

    setTimelineData([columns, ...aptuRows, ...suipRows]);
  }, [aptuData, singleUnitInitialPeriodsData, startDate, endDate, clampDates]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedData = () => {
    if (!aptuData.length) return [];

    return [...aptuData].sort((a, b) => {
      if (sortField === 'building.buildingName') {
        const aValue = a.building?.buildingName || '';
        const bValue = b.building?.buildingName || '';
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortField === 'startTime' || sortField === 'stopTime') {
        const aValue = sortField === 'startTime' ? a.startTime : (a.stopTime || '9999-12-31');
        const bValue = sortField === 'startTime' ? b.startTime : (b.stopTime || '9999-12-31');
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortField === 'id' || sortField === 'buildingId' || sortField === 'suiteTypeId' ||
          sortField === 'buildingBillingTypeId' || sortField === 'activeUnits') {
        const aValue = Number(a[sortField] || 0);
        const bValue = Number(b[sortField] || 0);
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aValue = String(a[sortField] || '');
      const bValue = String(b[sortField] || '');
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  };

  return (
    <Box sx={{ maxWidth: '1200px', margin: 'auto', padding: 2 }}>
      <div>
        <h1>Active Property Times Units Timeline</h1>
        <p>Partner ID: {PARTNER_ID}</p>
        <Card sx={{ p: 3, mb: 3 }}>
          <TimelineFilterControls
            buildingId={buildingId}
            setBuildingId={setBuildingId}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            clampDates={clampDates}
            setClampDates={setClampDates}
            fetchAPTUData={fetchAPTUData}
            isLoading={isLoading}
          />

          {timelineData.length > 1 ? (
            <APTUTimeline timelineData={timelineData} />
          ) : (
            <p>No data to display. Please load timeline data using the controls above.</p>
          )}

          {aptuData.length > 0 ? (
            <APTUTable
              aptuData={getSortedData()}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            />
          ) : (
            !isLoading && (
              <p style={{ textAlign: 'center', marginTop: '20px' }}>
                No records found. Try adjusting your filter criteria.
              </p>
            )
          )}

          {singleUnitInitialPeriodsData.length > 0 ? (
            <SingleUnitInitialPeriodsTable data={singleUnitInitialPeriodsData} />
          ) : (
            aptuData.length > 0 && (
              <p style={{ textAlign: 'center', marginTop: '20px' }}>
                No Single Unit Initial Period records found related to these APTUs.
              </p>
            )
          )}
        </Card>
      </div>
    </Box>
  );
};

export default TieredUnitCountTimelineContainer;
